<template>
  <div class="h-full">
    <div class="h-full flex flex-col pr-3 sm:pr-10">
      <div class="flex justify-end w-full"><PillButton outlined icon="send" text="Invite" primary @click="openInviteList" /></div>
      <TableElevated :items="users" :headers="headers" class="text-13 pr-3">
        <template v-slot:[`item.actions`]="{ item: user }">
          <IconButton v-if="user.email !== $store.state.user.email" icon="delete" class="text-primary" @click="onDeleteClick(user)" />
        </template>
        <template v-slot:[`item.status`]="{ item: user }">
          <Badge v-if="user.status" info :text="user.status"></Badge>
        </template>
        <template v-slot:[`item.resend`]="{ item: user }">
          <PillButton
            v-if="user.status && user.status === 'expired'"
            :loading="resendingEmail === user.email"
            :disabled="resendingEmail === user.email"
            class="ml-auto"
            small
            text="Resend"
            icon="send"
            outlined
            @click="onResendClick(user.email)"
          ></PillButton>
        </template>
        <template v-slot:[`item.role`]="{ item: user }">
          <div v-if="user.email !== $store.state.user.email" class="w-26">
            <v-select
              :modelValue="user.role"
              :options="getRoles"
              :reduce="(opt) => opt.value"
              :searchable="false"
              :clearable="false"
              class="w-25"
              dense
              text
              @update:modelValue="onRoleChange($event, user)"
            ></v-select>
          </div>
          <div v-else class="text-13 ml-3 opacity-80">
            {{ $t(`projects.roles.${user.role}`) }}
          </div>
        </template>
      </TableElevated>
    </div>
    <Modal v-if="fetching">
      <div class="flex flex-col font-700 items-center justify-center px-20 my-10">
        <div>{{ $t('loading_data') }}</div>
        <Icon name="loading_dots" class="w-14 mt-3 text-primary" />
      </div>
    </Modal>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import TeamInviteModal from '@/components/project/TeamInviteModal.vue';
import { TEAM_MEMBER_ROLES } from '@/constants';
import DeleteModal from '../../../modals/DeleteModal.vue';

export default {
  name: 'AuthenticationTeamMembers',
  data() {
    return {
      fetching: false,
      activeProvider: null,
      providerConfig: {},
      emailPasswordEnabled: false,
      resendingEmail: null,
    };
  },
  computed: {
    ...mapGetters(['getProjectSecrets', 'getProjectTeamMembers']),
    getRoles() {
      const roles = Object.values(TEAM_MEMBER_ROLES);
      return roles.map((role) => ({ label: this.$t(`projects.roles.${role}`), value: role }));
    },
    users() {
      return Object.values(this.getProjectTeamMembers());
    },
    projectId() {
      return this.$route.params.projectId;
    },
    secrets() {
      return Object.keys(this.getProjectSecrets()).map((i) => ({ label: i, value: i }));
    },
    headers() {
      return [
        { key: 'email', title: this.$t('email'), width: '250px' },
        { key: 'role', title: this.$t('role'), padding: '0 0 0 12px' },
        { key: 'resend', title: '', width: '50px', align: 'center' },
        { key: 'status', title: 'status', width: '50px', align: 'center' },
        { key: 'actions', title: this.$t('actions'), width: '50px' },
      ];
    },
  },
  methods: {
    ...mapActions(['showToastMessage', 'fetchProjectTeamMembers', 'resendProjectInviteEmail', 'changeProjectTeamMemberRole', 'deleteProjectInvitedMember']),
    async onRoleChange(value, user) {
      this.fetching = true;
      const res = await this.changeProjectTeamMemberRole({ project_id: this.projectId, email: user.email, role: value });
      if (res) {
        this.showToastMessage({ message: this.$t('projects.iam.role_changed'), type: 'success' });
        await this.fetchProjectTeamMembers({ project_id: this.projectId });
      }
      this.fetching = false;
    },
    async onResendClick(email) {
      this.resendingEmail = email;
      const response = await this.resendProjectInviteEmail({ project_id: this.projectId, email: email.trim() });
      this.resendingEmail = null;
      if (response) {
        this.showToastMessage({ message: this.$t('projects.iam.invite_send'), type: 'success' });
      }
    },
    onDeleteClick(user) {
      this.$showModal(DeleteModal, {
        title: this.$t('projects.iam.confirm_remove_user'),
        subtitle: this.$t('projects.iam.confirm_remove_user_subtitle'),
        onConfirm: async () => {
          await this.deleteProjectInvitedMember({ project_id: this.projectId, email: user.email });
        },
      });
    },
    openInviteList() {
      this.$showModal(TeamInviteModal, { projectId: this.projectId });
    },
  },
  watch: {
    projectId: {
      async handler() {
        this.fetching = true;
        await this.fetchProjectTeamMembers({ project_id: this.projectId });
        this.fetching = false;
      },
      immediate: true,
    },
  },
};
</script>
